import React, { useMemo, useState } from 'react';
import {
  FileArchive,
  FileImage,
  FileJson,
  Film,
  FileText,
  Images,
  MonitorPlay,
  Presentation,
} from 'lucide-react';
import type { ExportProgress } from '../types';
import { useEditorStore } from '../store/editor';
import { Modal } from './Modal';
import {
  cellToSvgString,
  downloadBlob,
  exportBoardPng,
  exportCellsZip,
  exportCurrentCellPng,
  exportGif,
  exportPdf,
  exportPptx,
  exportProjectPackage,
} from '../utils/export';

interface ExportOption {
  id: string;
  name: string;
  description: string;
  detail: string;
  icon: React.ReactNode;
  run: () => Promise<void>;
}

export const ExportDialog: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const activeCellId = useEditorStore((state) => state.activeCellId);
  const open = useEditorStore((state) => state.exportOpen);
  const setOpen = useEditorStore((state) => state.setExportOpen);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState('');
  const cell = project?.cells.find((item) => item.id === activeCellId) ?? project?.cells[0];

  const options = useMemo<ExportOption[]>(() => {
    if (!project || !cell) return [];
    const report = (value: ExportProgress) => setProgress(value);
    return [
      { id: 'cell-png', name: 'Current cell PNG', description: 'A high-resolution image of the active scene.', detail: 'PNG · 2× resolution', icon: <FileImage size={24} />, run: () => exportCurrentCellPng(project, cell) },
      { id: 'board-png', name: 'Complete board PNG', description: 'Every scene composed into a polished board.', detail: 'PNG · print-ready', icon: <Images size={24} />, run: () => exportBoardPng(project, report) },
      { id: 'cells-zip', name: 'Cell image pack', description: 'One high-resolution PNG per scene in a ZIP.', detail: 'ZIP · individual cells', icon: <FileArchive size={24} />, run: () => exportCellsZip(project, report) },
      { id: 'pdf', name: 'Storyboard PDF', description: 'One scene per page with titles for printing or review.', detail: 'PDF · multi-page', icon: <FileText size={24} />, run: () => exportPdf(project, report) },
      { id: 'pptx', name: 'PowerPoint deck', description: 'Each scene becomes an editable presentation slide.', detail: 'PPTX · widescreen', icon: <Presentation size={24} />, run: () => exportPptx(project, report) },
      { id: 'gif', name: 'Animated GIF', description: 'Play every scene using its configured duration.', detail: 'GIF · looping animation', icon: <Film size={24} />, run: () => exportGif(project, report) },
      { id: 'project', name: 'Portable project', description: 'A complete backup that can be imported on another device.', detail: '.smithboard · ZIP package', icon: <FileJson size={24} />, run: () => exportProjectPackage(project, report) },
      { id: 'svg', name: 'Current cell SVG', description: 'A scalable vector export of the active scene.', detail: 'SVG · editable vector', icon: <MonitorPlay size={24} />, run: async () => {
        const blob = new Blob([cellToSvgString(project, cell)], { type: 'image/svg+xml;charset=utf-8' });
        downloadBlob(blob, `${project.title}-${cell.title}.svg`.replace(/[\\/:*?"<>|]+/g, '-'));
      } },
    ];
  }, [cell, project]);

  const execute = async (option: ExportOption) => {
    setBusy(option.id);
    setError('');
    setProgress({ label: 'Preparing export', completed: 0, total: 1 });
    try {
      await option.run();
      setProgress({ label: 'Download ready', completed: 1, total: 1 });
      window.setTimeout(() => setProgress(null), 1400);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The export could not be completed.');
      setProgress(null);
    } finally {
      setBusy(null);
    }
  };

  const percentage = progress ? Math.round((progress.completed / Math.max(1, progress.total)) * 100) : 0;

  return (
    <Modal open={open} onClose={() => !busy && setOpen(false)} title="Export StoryboardSmith" subtitle="Everything is generated locally in this browser." className="export-modal">
      <div className="export-grid">
        {options.map((option) => (
          <button key={option.id} type="button" className="export-option" onClick={() => void execute(option)} disabled={Boolean(busy)}>
            <span className="export-option-icon">{option.icon}</span>
            <span className="export-option-copy"><strong>{busy === option.id ? 'Working…' : option.name}</strong><span>{option.description}</span><small>{option.detail}</small></span>
          </button>
        ))}
      </div>
      {progress && (
        <div className="export-progress" role="status" aria-live="polite">
          <div><strong>{progress.label}</strong><span>{percentage}%</span></div>
          <div className="progress-track"><span style={{ width: `${percentage}%` }} /></div>
        </div>
      )}
      {error && <div className="export-error" role="alert"><strong>Export failed</strong><span>{error}</span></div>}
      <p className="export-privacy-note">No file is uploaded to a server. Large boards may take several seconds while your device renders each scene.</p>
    </Modal>
  );
};
